import { v4 as uuidv4 } from 'uuid';
import { crdtServiceWS } from './crdtServiceWS';
import { createFileLogger } from '../../utils/logger';

export interface SessionData {
  x: number;
  y: number;
  user: {
    id: string;
    name: string;
    email?: string;
    picture?: string;
  };
  color: string;
  lastSeen: number;
}

class PresenceService {
  private sessionId: string;
  private currentSpaceId: string | null = null;
  private disconnectFn: (() => void) | null = null;
  private cleanupInterval: NodeJS.Timeout | null = null;
  private lastUpdateTime: number = 0;
  private throttleDelay: number = 100; // Throttle to max 10 updates per second

  constructor() {
    // Generate unique session ID for this tab
    this.sessionId = uuidv4();
    
    // Setup multiple cleanup events for reliability
    if (typeof window !== 'undefined') {
      const cleanupHandler = () => {
        this.immediateCleanup();
      };
      
      window.addEventListener('beforeunload', cleanupHandler);
      window.addEventListener('pagehide', cleanupHandler);
      window.addEventListener('unload', cleanupHandler);
    }
  }

  /**
   * Connect to a space and start receiving presence updates
   */
  connect(spaceId: string, onUpdate: (sessions: Record<string, SessionData>) => void): () => void {
    if (this.currentSpaceId) {
      this.disconnect();
    }

    this.currentSpaceId = spaceId;
    const key = `presence-${spaceId}`;

    // Connect to CRDT for real-time updates
    this.disconnectFn = crdtServiceWS.connect<Record<string, SessionData>>(key, (update) => {
      try {
        if (!update?.data || typeof update.data !== 'object') return;

        const now = Date.now();
        const activeSessions: Record<string, SessionData> = {};
        
        for (const [sessionId, session] of Object.entries(update.data)) {
          if (!session || typeof session !== 'object') continue;
          const s = session as Partial<SessionData>;
          if (typeof s.lastSeen !== 'number' || now - s.lastSeen >= 10000) continue;
          if (!s.user || typeof s.user !== 'object' || !s.user.id) continue;
          activeSessions[sessionId] = session as SessionData;
        }
        
        delete activeSessions[this.sessionId];
        
        onUpdate(activeSessions);
      } catch (error) {
        const log = createFileLogger('PRESENCE_SERVICE');
        log.error('Error processing presence update:', error);
      }
    });

    // Start periodic cleanup every 3 seconds for faster removal
    this.cleanupInterval = setInterval(() => {
      try {
        this.cleanupStalePresence(spaceId);
      } catch (error) {
        const log = createFileLogger('PRESENCE_SERVICE');
        log.error('Error in periodic cleanup:', error);
      }
    }, 3000);

    // Return disconnect function
    return () => this.disconnect();
  }

  /**
   * Update cursor position for this session
   */
  updateCursor(x: number, y: number, user: any): void {
    const now = Date.now();
    if (now - this.lastUpdateTime < this.throttleDelay) {
      return;
    }
    this.lastUpdateTime = now;
    
    if (!this.currentSpaceId) {
      return;
    }

    const key = `presence-${this.currentSpaceId}`;
    
    // Ensure all data is serializable and doesn't contain circular references
    const sessionData: SessionData = {
      x: Number(x) || 0,
      y: Number(y) || 0,
      user: {
        id: String(user?.sub || 'anonymous'),
        name: String(user?.name || 'Anonymous User'),
        email: user?.email ? String(user.email) : undefined,
        picture: user?.picture ? String(user.picture) : undefined,
      },
      color: this.getUserColor(user?.sub || 'anonymous'),
      lastSeen: Date.now(),
    };

    try {
      // Update immediately with no debouncing
      crdtServiceWS.update(key, {
        [this.sessionId]: sessionData
      }, {
        preserveOtherConnections: true,
        updateType: 'update'
      });
    } catch (error) {
      const log = createFileLogger('PRESENCE_SERVICE');
      log.error(`Error in updateCursor for key ${key}:`, error);
      // Don't rethrow to prevent breaking the app
    }
  }

  /**
   * Remove this session's presence
   */
  removePresence(): void {
    if (!this.currentSpaceId) return;

    const key = `presence-${this.currentSpaceId}`;
    
    try {
      const doc = crdtServiceWS.get(key);
      if (doc) {
        let flatData: Record<string, any>;
        try {
          flatData = doc.getMap(key).toJSON();
        } catch {
          flatData = {};
        }

        const toRemove: Record<string, null> = {};
        if (flatData && typeof flatData === 'object') {
          Object.keys(flatData).forEach(flatKey => {
            if (flatKey.startsWith(this.sessionId + '.')) {
              toRemove[flatKey] = null;
            }
          });
        }
        toRemove[this.sessionId] = null;
        
        crdtServiceWS.update(key, toRemove, {
          preserveOtherConnections: true,
          updateType: 'remove'
        });
      } else {
        crdtServiceWS.update(key, {
          [this.sessionId]: null
        }, {
          preserveOtherConnections: true,
          updateType: 'remove'
        });
      }
    } catch (error) {
      // Swallow — presence cleanup is best-effort
    }
  }

  /**
   * Disconnect from current space
   */
  disconnect(): void {
    if (this.disconnectFn) {
      this.disconnectFn();
      this.disconnectFn = null;
    }

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    this.removePresence();
    this.currentSpaceId = null;
  }

  /**
   * Cleanup stale presence data
   */
  private cleanupStalePresence(spaceId: string): void {
    const key = `presence-${spaceId}`;
    const doc = crdtServiceWS.get(key);
    if (!doc) return;

    let flatData: Record<string, any>;
    try {
      const ymap = doc.getMap(key);
      flatData = ymap.toJSON();
      if (!flatData || typeof flatData !== 'object') return;
    } catch {
      return;
    }

    const now = Date.now();
    const toRemove: Record<string, null> = {};
    const staleSessionIds = new Set<string>();

    for (const [flatKey, value] of Object.entries(flatData)) {
      if (flatKey.endsWith('.lastSeen') && typeof value === 'number') {
        const sessionId = flatKey.replace('.lastSeen', '');
        if (now - value > 10000) {
          staleSessionIds.add(sessionId);
        }
      }
    }

    staleSessionIds.forEach(sessionId => {
      Object.keys(flatData).forEach(flatKey => {
        if (flatKey.startsWith(sessionId + '.')) {
          toRemove[flatKey] = null;
        }
      });
      toRemove[sessionId] = null;
    });

    if (Object.keys(toRemove).length > 0) {
      crdtServiceWS.update(key, toRemove, {
        preserveOtherConnections: true,
        updateType: 'remove'
      });
    }
  }

  /**
   * Get a consistent color for a user
   */
  private getUserColor(userId: string): string {
    const colors = [
      '#FF5733', '#33FF57', '#3357FF', '#FF33A8', 
      '#33FFF6', '#F633FF', '#FFF633', '#FF8C33',
      '#8C33FF', '#33FF8C'
    ];
    
    // Use user ID to consistently assign same color
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = ((hash << 5) - hash + userId.charCodeAt(i)) & 0xffffffff;
    }
    
    return colors[Math.abs(hash) % colors.length];
  }

  immediateCleanup(): void {
    this.removePresence();
  }

  getSessionId(): string {
    return this.sessionId;
  }
}

export const presenceService = new PresenceService(); 